import {
  CheckOutlined,
  DownloadOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import { QrCodeIcon } from 'lucide-react';
import QRCode from 'qrcode';
import React from 'react';

const SCHEME = 'stag.vn';
const LINK_HOST = 'https://link.stag.vn';

interface QRItem {
  label: string;
  description: string;
  url: string;
  shareLink?: string;
}

const downloadQR = async (url: string, filename: string) => {
  const dataUrl = await QRCode.toDataURL(url, {
    width: 512,
    margin: 2,
    color: { dark: '#000000', light: '#ffffff' },
  });
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
};

const QRCard = ({ item }: { item: QRItem }) => {
  const [copied, setCopied] = React.useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(item.shareLink!);
    message.success('Đã copy link');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col items-center gap-2 p-3 border rounded-lg bg-white">
      <div className="flex items-center justify-center gap-1 w-full text-base">
        <div className="font-medium truncate">{item.label}</div>
        {item.shareLink && (
          <Tooltip title={copied ? 'Đã copy!' : `Copy link: ${item.shareLink}`}>
            {copied ? (
              <CheckOutlined className="text-green-500 flex-shrink-0" />
            ) : (
              <LinkOutlined
                className="text-gray-400 hover:text-blue-500 cursor-pointer flex-shrink-0"
                onClick={copyLink}
              />
            )}
          </Tooltip>
        )}
      </div>
      <Tooltip title={`Nhấn để tải QR -- ${item.url}`}>
        <div
          className="relative group cursor-pointer w-full max-w-56"
          onClick={() =>
            downloadQR(
              item.url,
              `qr-${item.label.toLowerCase().replace(/\s+/g, '-')}.png`
            )
          }
        >
          <QRCodePreview url={item.url} />
          <div className="absolute inset-0 bg-black/40 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <DownloadOutlined className="text-white text-2xl" />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

const QRCodePreview = ({ url }: { url: string }) => {
  const [dataUrl, setDataUrl] = React.useState<string>('');

  React.useEffect(() => {
    QRCode.toDataURL(url, { width: 160, margin: 1 }).then(setDataUrl);
  }, [url]);

  if (!dataUrl)
    return (
      <div className="w-full aspect-square bg-gray-100 animate-pulse rounded" />
    );
  return <img src={dataUrl} alt={url} className="w-full aspect-square" />;
};

interface StockAccountQRNavigateProps {
  stockAccountId: string | number;
  accountNumber?: string;
}

const StockAccountQRNavigate = ({
  stockAccountId,
  accountNumber,
}: StockAccountQRNavigateProps) => {
  const qrItems: QRItem[] = [
    {
      label: 'Quản lý tài khoản',
      description: 'manage-accounts',
      url: `${SCHEME}://manage-accounts`,
      shareLink: `${LINK_HOST}/manage-accounts`,
    },
    {
      label: 'Chi tiết tài khoản CK',
      description: `stock-account/${stockAccountId}`,
      url: `${SCHEME}://stock-account/${stockAccountId}`,
      shareLink: `${LINK_HOST}/stock-account/${stockAccountId}`,
    },
    {
      label: 'Ngân hàng liên kết',
      description: `stock-account/${stockAccountId}/banks`,
      url: `${SCHEME}://stock-account/${stockAccountId}/banks`,
      shareLink: `${LINK_HOST}/stock-account/${stockAccountId}/banks`,
    },
    ...(accountNumber
      ? [
          {
            label: 'Thêm tài khoản ngân hàng',
            description: `bank-account/create/${accountNumber}`,
            url: `${SCHEME}://bank-account/create/${accountNumber}`,
            shareLink: `${LINK_HOST}/bank-account/create/${accountNumber}`,
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center mb-1">
          <QrCodeIcon className="mr-1" size={18} />
          QR App Navigate
        </h3>
        <div>Cần tải ứng dụng rồi quét mã QR mới hoạt động.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {qrItems.map(item => (
          <QRCard key={item.url} item={item} />
        ))}
      </div>
    </div>
  );
};

export default StockAccountQRNavigate;
